import React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useCreateOrUpdateObjectDataset } from "api/api-hooks/object-dataset-hooks";
import { ObjectDatasetDto, ObjectDatasetReq, objectDatasetTypes } from "api/model/object-dataset/types";
import { ControllerDropdownInput, ControllerTextInput } from "components/basic/controller-inputs/ControllerInputs";
import useCurrentCompanyContext from "hooks/useCurrentCompanyContext";
import { Button } from "primereact/button";
import { Controller, useForm } from "react-hook-form";
import { T } from "services/lang/LanguageService";
import { IDENTIFIER_REGEX } from "utils/regex";
import { z } from "zod";

const schema = z.object({
  identifier: z
    .string()
    .nonempty()
    .regex(IDENTIFIER_REGEX, "Identifier must be alphanumeric with underscores and hyphens"),
  title: z.string().min(4).max(255),
  type: z.enum(objectDatasetTypes),
});

type ObjectDatasetFormProps = {
  closeDialog: () => void;
  objectDataset?: ObjectDatasetDto;
}

const ObjectDatasetForm: React.FC<ObjectDatasetFormProps> = ({ closeDialog, objectDataset }) => {
  const company = useCurrentCompanyContext();
  const createOrUpdate = useCreateOrUpdateObjectDataset();

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<ObjectDatasetDto>({
    values: objectDataset,
    resolver: zodResolver(schema),
  });

    const onSubmit = (data: ObjectDatasetDto) => {
      if (!company) return;

      const dto: ObjectDatasetReq = {
        identifier: data.identifier,
        title: data.title,
        type: data.type,
      };
      createOrUpdate.mutate(
        {
          body: { ...dto, identifier: objectDataset?.identifier ?? dto.identifier },
          companyRef: company.companyReference,
        },
        { onSuccess: closeDialog }
      );

    };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-wrap">
        <Controller
          name="identifier"
          control={control}
          render={({ field }) => (
            <ControllerTextInput field={field} errors={errors} label={T("word.identifier")} disabled={!!objectDataset} />
          )}
        />
        <Controller
          name="title"
          control={control}
          render={({ field }) => (
            <ControllerTextInput field={field} errors={errors} label={T("word.title")} />
          )}
        />
      </div>

      <div className="flex flex-wrap">
        <Controller
          defaultValue="HTML"
          name="type"
          control={control}
          render={({ field }) => (
            <ControllerDropdownInput
              options={objectDatasetTypes.map(type => ({ label: type, value: type }))}
              field={field}
              errors={errors}
              label={T("word.type")}
            />
          )}
        />
      </div>
      <div className="flex justify-content-end gap-2 mx-2">
        <Button
          label={T("action.cancel")}
          size="small"
          outlined
          severity="danger"
          type="button"
          onClick={closeDialog}
          />
        <Button
          label={objectDataset ? T("action.update") : T("action.create")}
          size="small"
          outlined
          loading={createOrUpdate.isPending}
        />
      </div>
    </form>
  );
}

export default ObjectDatasetForm;